import { createContext, Dispatch, memo, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { DentalCoverageResponse } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { useGetCachedDentalCoverageInfoByPersonId } from '@frontend/api-insurance-detail';
import type { InsuranceCoverageResponseDental } from '@frontend/api-insurance-eligibility-report';
import { useRemoveAppLoader } from '@frontend/document';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader } from '@frontend/design-system';
import { formattedDentalCoverageResponse } from '../../../helpers/formattedDentalCoverageTableData';
import { useInsuranceEligibilityReportContainerContext } from '../../../providers/insurance-report-container.provider';
import type { Filters } from '../../../types/tables-data-type';
import { getInsuranceBenefitKeys, getInsuranceReportAccordionIds } from '../helper';
import { initializeAllTableColumnFilters } from '../hooks/useInsuranceTableColumnFilters';
import { useInsuranceTableFiltersColumnsShallowStore } from '../hooks/useInsuranceTableFiltersColumnsStore';
import { UpdateTablesDataParamsType, useNetworkTypeFilterData } from '../hooks/useNetworkTypeFilterTableData';
import { SegregatedDataByNetworkType, TablesDataType, Vendors } from '../types/types';

type DentalInsuranceReportContextState = {
  isLoading: boolean;
  filters: Filters;
  isPrintDialogVisible: boolean;
  hidePrintDialog: () => void;
  setFilters: Dispatch<SetStateAction<Filters>>;
  vendor: Vendors | null;
  tablesData: TablesDataType;
  segregatedTablesDataByNetwork: SegregatedDataByNetworkType;
  insuranceEligibilityReportData: DentalCoverageResponse;
  updateTablesData: (params: UpdateTablesDataParamsType) => void | null;
};

export const DentalInsuranceReportContext = createContext<DentalInsuranceReportContextState>({
  isLoading: false,
  isPrintDialogVisible: false,
  hidePrintDialog: () => {},
  filters: {
    inNetworkSelected: true,
    coverage: [],
  },
  setFilters: () => {},
  updateTablesData: () => {},
  vendor: null,
  tablesData: {},
  insuranceEligibilityReportData: {},
  segregatedTablesDataByNetwork: {
    in_network: {},
    out_network: {},
  },
});

type DentalInsuranceReportContextProviderProps = {
  children: React.ReactNode;
  isPrintDialogVisible: boolean;
  hidePrintDialog: () => void;
};

export const DentalInsuranceReportContextProvider = memo(
  ({ children, isPrintDialogVisible, hidePrintDialog }: DentalInsuranceReportContextProviderProps) => {
    const [allTablesData, setAllTablesData] = useState<TablesDataType>({});
    const [filters, setFilters] = useState<Filters>({
      inNetworkSelected: true,
      coverage: [],
    });
    const { t } = useTranslation('insuranceEligibilityReport');
    const { removeAppLoader } = useRemoveAppLoader({ removeOnMount: false });

    const { setColumnsFilters } = useInsuranceTableFiltersColumnsShallowStore('setColumnsFilters');

    const { onApiFetchFailure, personId, locationId } = useInsuranceEligibilityReportContainerContext();
    const {
      data: dentalCoverageResponseUntyped,
      isError: DentalCoverageError,
      isLoading: isLoadingDentalInsuranceDetails,
    } = useGetCachedDentalCoverageInfoByPersonId(personId, locationId, InsuranceType.INSURANCE_TYPE_PRIMARY);
    const dentalCoverageResponse = dentalCoverageResponseUntyped as InsuranceCoverageResponseDental;
    //If the dentalCoverageResponse will have partner as Tuuthfairy then it will render it as Tuuthfairy report or else DentalXChange
    const vendor: Vendors =
      dentalCoverageResponse?.partner === Vendors.TuuthFairy ? Vendors.TuuthFairy : Vendors.DentalXChange;

    const isLoading = isLoadingDentalInsuranceDetails;

    const { filteredTablesData, segregatedDataByNetwork, updateTablesData } = useNetworkTypeFilterData({
      allTablesData,
      isLoading,
      vendor,
    });

    const providerValue = useMemo<DentalInsuranceReportContextState>(() => {
      return {
        isLoading,
        filters,
        isPrintDialogVisible,
        tablesData: filteredTablesData || {},
        segregatedTablesDataByNetwork: segregatedDataByNetwork,
        vendor,
        insuranceEligibilityReportData: dentalCoverageResponse || ({} as DentalCoverageResponse),
        updateTablesData: updateTablesData,
        setFilters,
        hidePrintDialog,
      };
    }, [
      isLoading,
      filters,
      isPrintDialogVisible,
      filteredTablesData,
      updateTablesData,
      segregatedDataByNetwork,
      vendor,
      dentalCoverageResponse,
      hidePrintDialog,
    ]);

    useEffect(() => {
      if (DentalCoverageError) {
        onApiFetchFailure?.();
      }
    }, [DentalCoverageError]);

    // Removing the app loader once data is loaded
    useEffect(() => {
      if (!isLoadingDentalInsuranceDetails) {
        // Removing the app loader once data is loaded
        removeAppLoader();

        setAllTablesData(formattedDentalCoverageResponse(dentalCoverageResponse, vendor));
      }
    }, [isLoadingDentalInsuranceDetails]);

    useEffect(() => {
      const accordionSectionList = getInsuranceBenefitKeys(allTablesData);
      const { accordionIds } = getInsuranceReportAccordionIds(accordionSectionList);
      //initializing all tables column filters and list
      initializeAllTableColumnFilters(
        setColumnsFilters,
        vendor as Vendors,
        accordionIds,
        locationId,
        Object.keys(allTablesData) as (keyof TablesDataType)[]
      );
    }, [allTablesData]);

    return (
      <DentalInsuranceReportContext.Provider value={providerValue}>
        <ContentLoader show={isLoadingDentalInsuranceDetails} message={t('Loading Insurance Data')} />
        {!isLoadingDentalInsuranceDetails && children}
      </DentalInsuranceReportContext.Provider>
    );
  }
);

DentalInsuranceReportContextProvider.displayName = 'DentalInsuranceReportContextProvider';

export const useDentalInsuranceReportContext = () => useContext(DentalInsuranceReportContext);
